/**
 * Single source of truth for league code → display name on the frontend.
 *
 * Mirrors `backend/src/config/sports-config.ts` (which is the canonical
 * map for the API). Kept here as a separate file so:
 *   - Edge runtime OG routes can import without pulling backend deps
 *   - Client components can render PM-style labels ("Serie B" not "ITSB")
 *     without each maintaining their own truncated copy. We had three
 *     parallel copies before — Sports2View, PlayerProfileClient, and
 *     the OG endpoint — and `itsb` was missing from all three, so the
 *     player profile rendered "ITSB" instead of "Serie B".
 *
 * When PM ships a new league code, add it here once.
 */
export const LEAGUE_NAMES: Record<string, string> = {
  // Basketball
  nba: "NBA",
  ncaab: "NCAAB",
  cbb: "NCAAB",
  wnba: "WNBA",
  euroleague: "Euroleague",
  bkcl: "Basketball CL",
  bkligend: "Liga Endesa",
  bkseriea: "Serie A (BB)",
  bknbl: "NBL",
  bkcba: "CBA",
  bkfr1: "Pro A",
  bkarg: "LNB",
  bkkbl: "KBL",
  bkbsl: "BSL",

  // American Football
  nfl: "NFL",
  cfb: "College Football",

  // Soccer — top European
  epl: "Premier League",
  lal: "La Liga",
  laliga: "La Liga",
  bun: "Bundesliga",
  bundesliga: "Bundesliga",
  sea: "Serie A",
  seriea: "Serie A",
  fl1: "Ligue 1",
  ligue1: "Ligue 1",
  ucl: "Champions League",
  uel: "Europa League",
  ucol: "Conference League",
  ten: "MLS",
  mls: "MLS",

  // Soccer — other
  ere: "Eredivisie",
  arg: "Liga Argentina",
  itc: "Coppa Italia",
  itsb: "Serie B",         // Italy Serie B — was missing, rendered as "ITSB"
  mex: "Liga MX",
  lib: "Copa Libertadores",
  sud: "Copa Sudamericana",
  tur: "Super Lig",
  rus: "Russian PL",
  efa: "FA Cup",
  efl: "EFL Cup",
  efc: "EFL Cup",
  cdr: "Copa del Rey",
  ssc: "Scottish PL",
  acn: "Africa Cup",
  col: "Liga Colombia",
  cde: "Copa de Espana",
  dfb: "DFB Pokal",
  bra: "Brasileirao",
  bra2: "Brazil Serie B",
  jap: "J-League",
  ja2: "J2 League",
  j1100: "J-League",
  j2100: "J2 League",
  kor: "K-League",
  csl: "Chinese Super League",
  chi: "Chinese Super League",
  spl: "Saudi PL",
  chi1: "Primera Chile",
  aus: "A-League",
  ind: "ISL",
  nor: "Eliteserien",
  den: "Superliga DK",
  por: "Liga Portugal",
  she: "Super League GR",
  sasa: "Saudi Cup",
  lpl: "Liga Pro",
  psp: "Liga Portugal 2",
  mar1: "Botola Pro",
  egy1: "Egyptian PL",
  cze1: "Czech 1. Liga",
  bol1: "Division Prof",
  rou1: "Liga 1 RO",
  per1: "Liga 1 Peru",
  col1: "Liga Col",
  afc: "AFC CL",
  ofc: "OFC CL",
  fif: "FIFA Club WC",
  fifwc: "FIFA World Cup",
  facup: "FA Cup",
  bl2: "Bundesliga 2",
  conl: "CONCACAF",
  es2: "La Liga 2",
  scop: "Scottish Cup",
  fr2: "Ligue 2",
  copa: "Copa America",
  uwcl: "Women's CL",
  ukr1: "Ukrainian PL",

  // Hockey
  nhl: "NHL",
  ahl: "AHL",
  khl: "KHL",
  shl: "SHL",
  dehl: "DEL",
  cehl: "Czech Extraliga",
  snhl: "Swiss NL",

  // Baseball
  mlb: "MLB",
  kbo: "KBO",

  // Tennis
  atp: "ATP",
  wta: "WTA",

  // Combat sports
  ufc: "UFC",
  boxing: "Boxing",

  // Cricket
  cricipl: "IPL",
  criclcl: "Lanka Premier",
  cricpsl: "PSL",
  crint: "Cricket Intl",

  // Motorsports
  formula1: "Formula 1",
  f1: "Formula 1",
  motogp: "MotoGP",

  // Esports — keep both pre and post `TICKER_TO_TEAMS_LEAGUE` codes so
  // labels work whether the consumer reads from gamma tags or the
  // remapped team-API code.
  lol: "LoL",
  cs2: "CS2",
  csgo: "CS2",
  dota2: "Dota 2",
  val: "Valorant",
  valorant: "Valorant",
  hok: "Honor of Kings",
  kog: "Honor of Kings",
  mlbb: "Mobile Legends",
  ow: "Overwatch",
  r6siege: "R6 Siege",
  rl: "Rocket League",
  starcraft2: "StarCraft II",
  codmw: "Call of Duty",
};

/** Look up a PM-style display label for a league code. Returns the
 *  upper-cased code when unknown so the caller still has *something*
 *  to render — better than blank. */
export function leagueLabel(league: string | null | undefined): string {
  if (!league) return "";
  const key = String(league).toLowerCase();
  return LEAGUE_NAMES[key] || key.toUpperCase();
}
